import { preloadRoutes } from '../utils/routePreload';
import { OFFLINE_CACHE_FLAG, PWA_ROLE_STORAGE_KEY } from './config';
import { getPublicOfflineRoutes, getRoleOfflineManifest, normalizeRole } from './roleOfflineManifest';
import type { CacheStateSnapshot, SWCommand, SWCommandResult } from './types';

const WARM_ROUTE_FAILURES_KEY = 'aa:pwa:warm-route-failures:v1';
const FAILED_ROUTE_RETRY_COOLDOWN_MS = 60 * 60 * 1000;

type WarmFailureState = Record<string, number>;

async function getServiceWorkerTarget(): Promise<ServiceWorker | null> {
  if (!OFFLINE_CACHE_FLAG || typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return registration.active || registration.waiting || registration.installing || navigator.serviceWorker.controller || null;
  } catch {
    return null;
  }
}

async function sendSwCommand(command: SWCommand): Promise<SWCommandResult> {
  const target = await getServiceWorkerTarget();
  if (!target) {
    return { ok: false, error: 'service_worker_not_ready' };
  }

  return new Promise<SWCommandResult>((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(() => {
      resolve({ ok: false, error: 'timeout' });
    }, 8000);

    channel.port1.onmessage = (event: MessageEvent<SWCommandResult>) => {
      window.clearTimeout(timeout);
      resolve(event.data || { ok: false, error: 'empty_response' });
    };

    try {
      target.postMessage(command, [channel.port2]);
    } catch (error) {
      window.clearTimeout(timeout);
      resolve({ ok: false, error: error instanceof Error ? error.message : 'post_message_failed' });
    }
  });
}

function normalizeOfflineRoute(route: string): string | null {
  const trimmed = String(route || '').trim();
  if (!trimmed.startsWith('/')) return null;
  return trimmed;
}

async function isRouteCached(route: string): Promise<boolean> {
  if (typeof window === 'undefined' || !('caches' in window)) return false;

  try {
    const url = new URL(route, window.location.origin);

    const exact = await caches.match(url.toString(), { ignoreSearch: true });
    if (exact) return true;

    const pathOnly = await caches.match(url.pathname, { ignoreSearch: true });
    return Boolean(pathOnly);
  } catch {
    return false;
  }
}

async function getMissingCachedRoutes(routes: string[]): Promise<string[]> {
  const uniqueRoutes = [...new Set(routes.map(normalizeOfflineRoute).filter((route): route is string => Boolean(route)))];
  if (!uniqueRoutes.length) return [];
  if (typeof window === 'undefined' || !('caches' in window)) return uniqueRoutes;

  const cachedChecks = await Promise.all(uniqueRoutes.map((route) => isRouteCached(route)));
  return uniqueRoutes.filter((_, index) => !cachedChecks[index]);
}

function readWarmFailureState(): WarmFailureState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(WARM_ROUTE_FAILURES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as WarmFailureState;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
}

function writeWarmFailureState(state: WarmFailureState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(WARM_ROUTE_FAILURES_KEY, JSON.stringify(state));
  } catch {
    // Ignore localStorage write failures.
  }
}

function toFailureKey(scope: string, route: string): string {
  return `${scope}:${route}`;
}

function getRetryableRoutes(scope: string, routes: string[]): string[] {
  const state = readWarmFailureState();
  const now = Date.now();
  return routes.filter((route) => {
    const key = toFailureKey(scope, route);
    const failedAt = state[key];
    if (!failedAt) return true;
    return now - failedAt >= FAILED_ROUTE_RETRY_COOLDOWN_MS;
  });
}

function syncWarmFailureState(scope: string, allRoutes: string[], missingRoutes: string[]): void {
  const state = readWarmFailureState();
  const missingSet = new Set(missingRoutes);
  const now = Date.now();

  for (const route of allRoutes) {
    const key = toFailureKey(scope, route);
    if (missingSet.has(route)) {
      state[key] = now;
    } else {
      delete state[key];
    }
  }

  writeWarmFailureState(state);
}

function clearRoleWarmFailureState(role?: string): void {
  const state = readWarmFailureState();
  const next: WarmFailureState = {};

  for (const [key, value] of Object.entries(state)) {
    if (!key.startsWith('role:')) {
      next[key] = value;
      continue;
    }
    if (role && key.startsWith(`role:${normalizeRole(role)}:`)) {
      continue;
    }
    if (!role) {
      continue;
    }
    next[key] = value;
  }

  writeWarmFailureState(next);
}

function getLastRole(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = localStorage.getItem(PWA_ROLE_STORAGE_KEY);
    return value ? normalizeRole(value) : null;
  } catch {
    return null;
  }
}

function setLastRole(role: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PWA_ROLE_STORAGE_KEY, normalizeRole(role));
  } catch {
    // Ignore localStorage write failures.
  }
}

function clearLastRole(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(PWA_ROLE_STORAGE_KEY);
  } catch {
    // Ignore localStorage errors.
  }
}

export async function warmPublicCaches(): Promise<boolean> {
  if (!OFFLINE_CACHE_FLAG) return false;

  const routes = getPublicOfflineRoutes();
  const missingRoutes = await getMissingCachedRoutes(routes);
  if (!missingRoutes.length) return true;

  const scope = 'public';
  const retryableRoutes = getRetryableRoutes(scope, missingRoutes);
  if (!retryableRoutes.length) return false;

  preloadRoutes(retryableRoutes);
  const result = await sendSwCommand({ type: 'WARM_PUBLIC' });
  if (!result.ok) return false;

  const stillMissing = await getMissingCachedRoutes(routes);
  syncWarmFailureState(scope, routes, stillMissing);
  return stillMissing.length === 0;
}

export async function warmRoleCaches(role: string | null | undefined): Promise<boolean> {
  if (!OFFLINE_CACHE_FLAG) return false;
  const manifest = getRoleOfflineManifest(role);
  if (!manifest) return false;

  const previousRole = getLastRole();
  if (previousRole && previousRole !== manifest.role) {
    await purgePrivateCaches();
    clearRoleWarmFailureState(previousRole);
  }

  const missingRoutes = await getMissingCachedRoutes(manifest.routes);
  if (!missingRoutes.length) {
    syncWarmFailureState(`role:${manifest.role}`, manifest.routes, []);
    setLastRole(manifest.role);
    return true;
  }

  const scope = `role:${manifest.role}`;
  const retryableRoutes = getRetryableRoutes(scope, missingRoutes);
  if (!retryableRoutes.length) return false;

  preloadRoutes(retryableRoutes);
  const result = await sendSwCommand({
    type: 'WARM_ROLE',
    role: manifest.role,
    routes: retryableRoutes,
  });

  if (!result.ok) return false;

  const stillMissing = await getMissingCachedRoutes(manifest.routes);
  syncWarmFailureState(scope, manifest.routes, stillMissing);
  if (!stillMissing.length) {
    setLastRole(manifest.role);
    return true;
  }

  return false;
}

export async function purgePrivateCaches(): Promise<boolean> {
  if (!OFFLINE_CACHE_FLAG) return false;
  clearLastRole();
  clearRoleWarmFailureState();
  const result = await sendSwCommand({ type: 'PURGE_PRIVATE' });
  return result.ok;
}

export async function getCacheState(): Promise<CacheStateSnapshot | null> {
  if (!OFFLINE_CACHE_FLAG) return null;
  const result = await sendSwCommand({ type: 'GET_CACHE_STATE' });
  return result.ok ? result.state || null : null;
}

export const isOfflineCacheEnabled = OFFLINE_CACHE_FLAG;
